import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEntitlement } from "@/hooks/useEntitlement";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageSquare, ChevronUp, ChevronDown, Pin, Trash2, Reply, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const sb = supabase as any;

type DiscussionRow = {
  id: string;
  mcq_id: string;
  user_id: string;
  parent_id: string | null;
  body: string;
  is_pinned: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
};

type Profile = { id: string; full_name: string | null };

interface MCQDiscussionsProps {
  mcqId: string;
}

export function MCQDiscussions({ mcqId }: MCQDiscussionsProps) {
  const { user } = useAuth();
  const { isAdmin, hasActiveAccess, loading: entLoading } = useEntitlement();
  const queryClient = useQueryClient();
  const [newPost, setNewPost] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const canParticipate = !!user && (isAdmin || hasActiveAccess);

  const { data: discussions = [], isLoading } = useQuery({
    queryKey: ["mcq-discussions", mcqId],
    enabled: canParticipate,
    queryFn: async () => {
      const { data, error } = await sb
        .from("mcq_discussions")
        .select("*")
        .eq("mcq_id", mcqId)
        .eq("is_deleted", false)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as DiscussionRow[];
    },
  });

  const userIds = useMemo(
    () => Array.from(new Set(discussions.map((d) => d.user_id))),
    [discussions]
  );

  const { data: profiles = [] } = useQuery({
    queryKey: ["discussion-profiles", userIds],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await sb
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);
      if (error) return [] as Profile[];
      return data as Profile[];
    },
  });

  const profileMap = useMemo(() => {
    const m = new Map<string, string>();
    profiles.forEach((p) => m.set(p.id, p.full_name || "Anonymous"));
    return m;
  }, [profiles]);

  const discussionIds = useMemo(() => discussions.map((d) => d.id), [discussions]);

  const { data: votes = [] } = useQuery({
    queryKey: ["discussion-votes", discussionIds],
    enabled: discussionIds.length > 0,
    queryFn: async () => {
      const { data, error } = await sb
        .from("mcq_discussion_votes")
        .select("discussion_id, user_id, value")
        .in("discussion_id", discussionIds);
      if (error) throw error;
      return data as { discussion_id: string; user_id: string; value: number }[];
    },
  });

  const voteAggregates = useMemo(() => {
    const m = new Map<string, { score: number; userVote: number }>();
    votes.forEach((v) => {
      const cur = m.get(v.discussion_id) ?? { score: 0, userVote: 0 };
      cur.score += v.value;
      if (v.user_id === user?.id) cur.userVote = v.value;
      m.set(v.discussion_id, cur);
    });
    return m;
  }, [votes, user?.id]);

  const postMutation = useMutation({
    mutationFn: async ({ body, parent_id }: { body: string; parent_id: string | null }) => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await sb.from("mcq_discussions").insert({
        mcq_id: mcqId,
        user_id: user.id,
        parent_id,
        body: body.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcq-discussions", mcqId] });
      setNewPost("");
      setReplyText("");
      setReplyTo(null);
      toast.success("Posted");
    },
    onError: (e: any) => toast.error(e.message || "Failed to post"),
  });

  const voteMutation = useMutation({
    mutationFn: async ({ discussionId, value }: { discussionId: string; value: number }) => {
      if (!user) throw new Error("Not authenticated");
      const existing = voteAggregates.get(discussionId)?.userVote ?? 0;
      if (existing === value) {
        const { error } = await sb
          .from("mcq_discussion_votes")
          .delete()
          .eq("discussion_id", discussionId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await sb
          .from("mcq_discussion_votes")
          .upsert(
            { discussion_id: discussionId, user_id: user.id, value },
            { onConflict: "discussion_id,user_id" }
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discussion-votes", discussionIds] });
    },
    onError: (e: any) => toast.error(e.message || "Failed to vote"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (discussionId: string) => {
      const { error } = await sb
        .from("mcq_discussions")
        .update({ is_deleted: true })
        .eq("id", discussionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcq-discussions", mcqId] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e.message || "Failed to delete"),
  });

  const pinMutation = useMutation({
    mutationFn: async ({ discussionId, pin }: { discussionId: string; pin: boolean }) => {
      const { error } = await sb
        .from("mcq_discussions")
        .update({ is_pinned: pin })
        .eq("id", discussionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcq-discussions", mcqId] });
    },
  });

  // Build threaded tree
  const { roots, childrenMap } = useMemo(() => {
    const childrenMap = new Map<string, DiscussionRow[]>();
    const roots: DiscussionRow[] = [];
    discussions.forEach((d) => {
      if (d.parent_id) {
        const arr = childrenMap.get(d.parent_id) ?? [];
        arr.push(d);
        childrenMap.set(d.parent_id, arr);
      } else {
        roots.push(d);
      }
    });
    return { roots, childrenMap };
  }, [discussions]);

  if (entLoading) {
    return (
      <Card className="p-4 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
      </Card>
    );
  }

  if (!canParticipate) {
    return (
      <Card className="p-4 text-center text-sm text-muted-foreground">
        <MessageSquare className="h-5 w-5 mx-auto mb-2 opacity-60" />
        Discussions are available for subscribed users.
      </Card>
    );
  }

  const renderPost = (post: DiscussionRow, depth = 0) => {
    const agg = voteAggregates.get(post.id) ?? { score: 0, userVote: 0 };
    const authorName = profileMap.get(post.user_id) ?? "User";
    const initials = authorName.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
    const isOwner = user?.id === post.user_id;
    const children = childrenMap.get(post.id) ?? [];

    return (
      <div key={post.id} style={{ marginLeft: depth > 0 ? `${Math.min(depth, 3) * 16}px` : 0 }}>
        <Card className={`p-3 ${post.is_pinned ? "border-primary/50 bg-primary/5" : ""}`}>
          <div className="flex gap-3">
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => voteMutation.mutate({ discussionId: post.id, value: 1 })}
                className={`p-1 rounded hover:bg-muted ${agg.userVote === 1 ? "text-primary" : "text-muted-foreground"}`}
                aria-label="Upvote"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium tabular-nums">{agg.score}</span>
              <button
                onClick={() => voteMutation.mutate({ discussionId: post.id, value: -1 })}
                className={`p-1 rounded hover:bg-muted ${agg.userVote === -1 ? "text-destructive" : "text-muted-foreground"}`}
                aria-label="Downvote"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{authorName}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </span>
                {post.is_pinned && (
                  <Badge variant="secondary" className="gap-1 text-xs">
                    <Pin className="h-3 w-3" /> Pinned
                  </Badge>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap break-words">{post.body}</p>

              <div className="flex items-center gap-1 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => {
                    setReplyTo(replyTo === post.id ? null : post.id);
                    setReplyText("");
                  }}
                >
                  <Reply className="h-3 w-3 mr-1" /> Reply
                </Button>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => pinMutation.mutate({ discussionId: post.id, pin: !post.is_pinned })}
                  >
                    <Pin className="h-3 w-3 mr-1" />
                    {post.is_pinned ? "Unpin" : "Pin"}
                  </Button>
                )}
                {(isOwner || isAdmin) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm("Delete this post?")) deleteMutation.mutate(post.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {replyTo === post.id && (
                <div className="mt-3 space-y-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    rows={2}
                    className="text-sm"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReplyTo(null);
                        setReplyText("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={!replyText.trim() || postMutation.isPending}
                      onClick={() => postMutation.mutate({ body: replyText, parent_id: post.id })}
                    >
                      Reply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {children.length > 0 && (
          <div className="mt-2 space-y-2">
            {children.map((c) => renderPost(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4" />
        <h3 className="font-semibold text-sm">
          Discussion {discussions.length > 0 && <span className="text-muted-foreground">({discussions.length})</span>}
        </h3>
      </div>

      <div className="space-y-2">
        <Textarea
          value={newPost}
          onChange={(e) => setNewPost(e.target.value)}
          placeholder="Share your thoughts on this question..."
          rows={3}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!newPost.trim() || postMutation.isPending}
            onClick={() => postMutation.mutate({ body: newPost, parent_id: null })}
          >
            {postMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : roots.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No discussion yet. Be the first to share your thoughts.
        </p>
      ) : (
        <div className="space-y-3">{roots.map((r) => renderPost(r))}</div>
      )}
    </div>
  );
}
